import * as THREE from "three";
import { makeCircle, setupOrtho } from "@core/three-helpers";
import { distanceToLoop, fillLoop, type Vec2Like } from "./band-shape";
import { loadLevels, getLevel, nextLevelIndex, levelNumber } from "@core/level-loader";
import { levels as levelData } from "./levels";

/**
 * Rubber Tire Jam — V0 (tangle field only).
 *
 * Port of the Unity reference (unity/Assets/RubberTireJam/...) to three.js.
 * A "rubber band" is a closed elastic loop hugging the OUTSIDE of N pins,
 * with straight tangent segments between consecutive pins joined by short
 * outer arcs at every pin. Bands cross in 2D with a z-order (sortOrder /
 * renderOrder) that determines which sits above which. A band can be
 * "lifted off" only when no other unreleased band with a HIGHER sortOrder
 * shares one of its pins.
 *
 * V0 deliberately ships only the tangle field + release flight; the
 * conveyor / cars layer from the previous prototype is intentionally
 * removed and will return in a follow-up.
 */

const canvas = document.getElementById("game") as HTMLCanvasElement;
const hudLevel = document.getElementById("hud-level")!;
const hudStatus = document.getElementById("hud-status")!;
const banner = document.getElementById("banner")!;

const { scene, camera, renderer, worldFromEvent } = setupOrtho(canvas, {
  virtualHeight: 22,
  background: 0x0e1a2a, // mirrors Unity cam background (14,26,42)
});

// ---------------------------------------------------------------------------
// Constants matching unity/Assets/RubberTireJam/Editor/BuildRubberTireJamScene.cs
// ---------------------------------------------------------------------------
const PIN_RADIUS = 0.28; // visual disc radius (Unity sprite scale 0.55 ≈ 0.275 world units)
const PICK_THRESHOLD = 0.5;
const WRAP_OFFSET = 0.42;
const ARC_SEGMENTS_PER_CORNER = 8;
const STRAND_COUNT = 5; // multi-strand to fake line thickness on platforms that clamp gl_LineWidth
const STRAND_STEP = 0.035; // world-units between adjacent strands
const RELEASE_FLIGHT_MS = 700;
const FLASH_INTERVAL_MS = 70;
const FLASH_COUNT = 3;

// ---------------------------------------------------------------------------
// Runtime state
// ---------------------------------------------------------------------------
interface Pin {
  index: number;
  position: THREE.Vector2;
  bands: Band[];
}

interface Band {
  id: number;
  color: number;
  sortOrder: number;
  pins: Pin[];
  released: boolean;
  releasing: boolean;
  flashing: boolean;
  /** N line-loops drawn at small perpendicular offsets to fake stroke width. */
  strands: THREE.LineLoop[];
  /** Last computed loop polyline (open — no duplicate close vertex). */
  loop: THREE.Vector3[];
}

let pins: Pin[] = [];
let bands: Band[] = [];
let nextBandId = 1;
let currentLevelIdx = 0;
let inputLocked = false;
const allLevels = loadLevels(levelData);

// ---------------------------------------------------------------------------
// Scene scaffolding
// ---------------------------------------------------------------------------
function clearScene(): void {
  // Bands
  for (const b of bands) {
    for (const s of b.strands) {
      scene.remove(s);
      s.geometry.dispose();
      (s.material as THREE.Material).dispose();
    }
  }
  bands = [];

  // Everything else tagged as level content (pins + inner dots)
  const toRemove: THREE.Object3D[] = [];
  scene.traverse((obj) => {
    if (obj.userData["levelContent"]) toRemove.push(obj);
  });
  for (const obj of toRemove) {
    scene.remove(obj);
    if (obj instanceof THREE.Mesh) {
      obj.geometry.dispose();
      (obj.material as THREE.Material).dispose();
    }
  }
  pins = [];
}

function makePin(index: number, x: number, y: number): Pin {
  const disc = makeCircle(PIN_RADIUS, 0xffffff, 32);
  disc.position.set(x, y, 0.5);
  disc.renderOrder = 1000; // pins always above bands
  disc.userData["levelContent"] = true;
  scene.add(disc);

  // Inner dark dot — gives pins a tire-post feel.
  const inner = makeCircle(PIN_RADIUS * 0.55, 0x0e1a2a, 24);
  inner.position.set(x, y, 0.51);
  inner.renderOrder = 1001;
  inner.userData["levelContent"] = true;
  scene.add(inner);

  return { index, position: new THREE.Vector2(x, y), bands: [] };
}

function makeBand(color: number, sortOrder: number, pinList: Pin[]): Band {
  const band: Band = {
    id: nextBandId++,
    color,
    sortOrder,
    pins: pinList,
    released: false,
    releasing: false,
    flashing: false,
    strands: [],
    loop: [],
  };
  for (const p of pinList) {
    if (!p.bands.includes(band)) p.bands.push(band);
  }

  // gl_LineWidth is clamped to 1 on most platforms, so we approximate
  // stroke thickness by stacking N LineLoops at small outward offsets.
  for (let i = 0; i < STRAND_COUNT; i++) {
    const geom = new THREE.BufferGeometry();
    const mat = new THREE.LineBasicMaterial({
      color,
      transparent: true,
      opacity: 1,
      depthTest: false,
    });
    const line = new THREE.LineLoop(geom, mat);
    // renderOrder = sortOrder * 100 + strand-index → higher sortOrder draws on top.
    line.renderOrder = sortOrder * 100 + i;
    line.userData["levelContent"] = true;
    scene.add(line);
    band.strands.push(line);
  }

  bands.push(band);
  redrawBand(band);
  return band;
}

function redrawBand(band: Band): void {
  const pinPositions: Vec2Like[] = band.pins.map((p) => ({ x: p.position.x, y: p.position.y }));
  fillLoop(pinPositions, WRAP_OFFSET, ARC_SEGMENTS_PER_CORNER, band.loop);

  const n = band.loop.length;
  if (n === 0) {
    for (const strand of band.strands) {
      strand.geometry.setAttribute("position", new THREE.BufferAttribute(new Float32Array(0), 3));
    }
    return;
  }

  for (let s = 0; s < band.strands.length; s++) {
    const strand = band.strands[s];
    const mid = (band.strands.length - 1) / 2;
    const offsetK = (s - mid) * STRAND_STEP;
    const positions = new Float32Array(n * 3);
    for (let i = 0; i < n; i++) {
      const cur = band.loop[i];
      const prev = band.loop[(i - 1 + n) % n];
      const next = band.loop[(i + 1) % n];
      const tx = next.x - prev.x;
      const ty = next.y - prev.y;
      const tlen = Math.max(1e-6, Math.hypot(tx, ty));
      // CCW loop → outward normal = (-ty, tx). For CW it would point inward,
      // but fillLoop produces CCW after winding detection so this is safe.
      const nx = -ty / tlen;
      const ny = tx / tlen;
      positions[i * 3] = cur.x + nx * offsetK;
      positions[i * 3 + 1] = cur.y + ny * offsetK;
      positions[i * 3 + 2] = cur.z;
    }
    strand.geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    strand.geometry.computeBoundingSphere();
  }
}

/**
 * Strict port of `Band.IsBlocked` (Band.cs lines 76-89). A band is blocked
 * if any OTHER non-released band that shares a pin with it has a higher
 * sortOrder.
 */
function isBlocked(band: Band): boolean {
  if (band.released) return false;
  for (const pin of band.pins) {
    for (const other of pin.bands) {
      if (other === band) continue;
      if (other.released) continue;
      if (other.sortOrder > band.sortOrder) return true;
    }
  }
  return false;
}

function detachFromPins(band: Band): void {
  for (const p of band.pins) {
    const idx = p.bands.indexOf(band);
    if (idx >= 0) p.bands.splice(idx, 1);
  }
  band.pins = [];
  band.released = true;
}

function setBandColor(band: Band, hex: number): void {
  for (const strand of band.strands) {
    (strand.material as THREE.LineBasicMaterial).color.setHex(hex);
  }
}

// ---------------------------------------------------------------------------
// Input
// ---------------------------------------------------------------------------
function onPointerDown(ev: PointerEvent): void {
  if (inputLocked) return;
  const world = worldFromEvent(ev);
  let best: Band | null = null;
  let bestOrder = Number.NEGATIVE_INFINITY;
  for (const b of bands) {
    if (b.released || b.releasing) continue;
    const d = distanceToLoop(b.loop, world.x, world.y);
    if (d > PICK_THRESHOLD) continue;
    if (b.sortOrder > bestOrder) {
      best = b;
      bestOrder = b.sortOrder;
    }
  }
  if (!best) return;

  if (isBlocked(best)) {
    flashBand(best);
    hudStatus.textContent = "Blocked — lift a band above this one first";
    return;
  }
  hudStatus.textContent = "Lifting…";
  releaseBand(best);
}

function flashBand(band: Band): void {
  if (band.flashing) return;
  band.flashing = true;
  const original = band.color;
  let flashes = 0;
  const tick = () => {
    if (flashes >= FLASH_COUNT * 2) {
      setBandColor(band, original);
      band.flashing = false;
      return;
    }
    setBandColor(band, flashes % 2 === 0 ? 0xffffff : original);
    flashes++;
    setTimeout(tick, FLASH_INTERVAL_MS);
  };
  tick();
}

function releaseBand(band: Band): void {
  band.releasing = true;
  detachFromPins(band);

  // Re-draw siblings (no-op in our geometry since each band's polyline
  // depends only on its own pins, but kept as a structural mirror of the
  // Unity BandManager.ReleaseBand flow).
  for (const other of bands) {
    if (other === band || other.released) continue;
    redrawBand(other);
  }

  flyOff(band, () => {
    for (const s of band.strands) {
      scene.remove(s);
      s.geometry.dispose();
      (s.material as THREE.Material).dispose();
    }
    const idx = bands.indexOf(band);
    if (idx >= 0) bands.splice(idx, 1);
    hudStatus.textContent = bands.length === 0 ? "Cleared!" : "Tap a band to lift it";
    maybeWin();
  });
}

function flyOff(band: Band, onDone: () => void): void {
  interface Snapshot {
    strand: THREE.LineLoop;
    startPositions: Float32Array;
    center: THREE.Vector3;
  }
  const snapshots: Snapshot[] = [];
  for (const strand of band.strands) {
    const attr = strand.geometry.getAttribute("position") as THREE.BufferAttribute | undefined;
    if (!attr) continue;
    const arr = new Float32Array(attr.array.length);
    arr.set(attr.array as Float32Array);
    const center = new THREE.Vector3();
    const count = attr.count;
    for (let i = 0; i < count; i++) {
      center.x += arr[i * 3];
      center.y += arr[i * 3 + 1];
      center.z += arr[i * 3 + 2];
    }
    center.divideScalar(Math.max(1, count));
    snapshots.push({ strand, startPositions: arr, center });
  }
  if (snapshots.length === 0) {
    onDone();
    return;
  }

  const meanCenter = new THREE.Vector3();
  for (const s of snapshots) meanCenter.add(s.center);
  meanCenter.divideScalar(snapshots.length);

  // Target: 6 world-units above centroid (mirrors Unity ReleaseTarget at y=+6.5).
  const target = meanCenter.clone();
  target.y += 6.5;

  const startTime = performance.now();
  const step = (now: number) => {
    const k = Math.min(1, (now - startTime) / RELEASE_FLIGHT_MS);
    const ease = 1 - Math.pow(1 - k, 3);
    const shift = target.clone().sub(meanCenter).multiplyScalar(ease);
    const shrink = 1 - 0.7 * ease;
    const fade = 1 - ease;

    for (const snap of snapshots) {
      const attr = snap.strand.geometry.getAttribute("position") as THREE.BufferAttribute;
      const arr = attr.array as Float32Array;
      const startArr = snap.startPositions;
      const count = attr.count;
      for (let i = 0; i < count; i++) {
        const sx = startArr[i * 3];
        const sy = startArr[i * 3 + 1];
        const sz = startArr[i * 3 + 2];
        const relX = (sx - meanCenter.x) * shrink;
        const relY = (sy - meanCenter.y) * shrink;
        const relZ = (sz - meanCenter.z) * shrink;
        arr[i * 3] = meanCenter.x + shift.x + relX;
        arr[i * 3 + 1] = meanCenter.y + shift.y + relY;
        arr[i * 3 + 2] = meanCenter.z + shift.z + relZ;
      }
      attr.needsUpdate = true;
      (snap.strand.material as THREE.LineBasicMaterial).opacity = fade;
    }

    if (k < 1) {
      requestAnimationFrame(step);
    } else {
      onDone();
    }
  };
  requestAnimationFrame(step);
}

// ---------------------------------------------------------------------------
// Level construction (mirrors BuildRubberTireJamScene.cs)
// ---------------------------------------------------------------------------
function spawnLevel(levelIdx: number): void {
  clearScene();
  currentLevelIdx = levelIdx;
  inputLocked = false;

  const cfg = getLevel(allLevels, levelIdx);
  pins = cfg.pins.map((p, i) => makePin(i, p.x, p.y));
  for (const band of cfg.bands) {
    const bandPins = band.pinIndices.map((idx) => pins[idx]);
    makeBand(band.color, band.sortOrder, bandPins);
  }

  hudLevel.textContent = `Level ${levelNumber(levelIdx)}`;
  hudStatus.textContent = "Tap a band to lift it. Topmost first.";
}

function showBanner(text: string, durationMs: number): void {
  banner.textContent = text;
  banner.classList.add("show");
  setTimeout(() => banner.classList.remove("show"), durationMs);
}

function maybeWin(): void {
  if (bands.length === 0) {
    inputLocked = true;
    showBanner(`Level ${levelNumber(currentLevelIdx)} clear!`, 1500);
    setTimeout(() => {
      spawnLevel(nextLevelIndex(allLevels, currentLevelIdx));
    }, 1600);
  }
}

canvas.addEventListener("pointerdown", onPointerDown);
spawnLevel(currentLevelIdx);

function loop(): void {
  requestAnimationFrame(loop);
  renderer.render(scene, camera);
}
loop();
